<template>
    <div class="glossary-page">
        <aside class="glossary-sidebar">
            <GlossaryNavigation :activeLetter="activeLetter" :availableLetters="availableLetters"
                @letter-selected="jumpToLetter" />
        </aside>
        <main class="glossary-main-content">
            <h1>Firefighting Glossary</h1>
            <div class="search-bar">
                <input type="text" v-model="searchTerm" placeholder="Search (e.g., Pumper, Ladder)"
                    @input="clearLetterSelection" />
            </div>

            <!-- Displaying ALL types by default, then potentially filtering by search -->
            <div v-if="filteredTerms.length > 0">
                <div v-for="letter in alphabet" :key="letter" :id="`glossary-section-${letter}`"
                    class="glossary-section">
                    <!-- Only show header if there are terms for this letter -->
                    <h2 class="glossary-section-title" v-if="getTermsForLetter(letter).length > 0">{{ letter }}</h2>
                    <div v-for="term in getTermsForLetter(letter)" :key="term.id" class="glossary-item">
                        <v-card class="glossary-item-wrapper" elevation="4">
                            <v-card-title class="text-wrap glossary-card-title">
                                <h3>{{ term.name }}</h3>
                            </v-card-title>
                            <div class="text-wrap glossary-description" v-html="term.description"></div>
                            <v-divider></v-divider>
                            <v-card-text>
                                <div class="glossary-details">
                                    <div v-if="term.prefix" class="glossary-detail-row">
                                        <div class="glossary-detail-label"><b>Unit Prefix:</b></div>
                                        <div class="glossary-detail-value">{{ term.prefix }}</div>
                                    </div>
                                    <div v-if="term.aliases && term.aliases.length > 0" class="glossary-detail-row">
                                        <div class="glossary-detail-label"><b>Aliases:</b></div>
                                        <div class="glossary-detail-value">{{ term.aliases.join(", ") }}</div>
                                    </div>
                                    <div v-if="term.examples && term.examples.length > 0" class="glossary-detail-row">
                                        <div class="glossary-detail-label"><b>Examples:</b></div>
                                        <div class="glossary-detail-value">
                                            <div v-if="term.example_image_links && term.example_image_links.length > 0">
                                                <span v-for="(example, index) in term.examples" :key="index">
                                                    <a :href="term.example_image_links[index]" target="_blank"
                                                        rel="noopener noreferrer">
                                                        {{ example }}
                                                    </a><span v-if="index < term.examples.length - 1">, </span>
                                                </span>
                                            </div>
                                            <div v-else>
                                                {{ term.examples.join(", ") }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </div>
            </div>
            <div v-else class="no-terms">
                <p>
                    No unit types found matching your search. Please adjust your search.
                </p>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import GlossaryNavigation from "@/components/GlossaryNavigation.vue";

const allGlossaryItems = [
    {
        id: "pumper",
        prefix: "P",
        name: "Pumper",
        description: "Has firefighting gear and basic rescue equipment.",
        aliases: ["Engine"],
        examples: ["P111", "P221", "P333", "P443"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Pumper-111/i-pwjtSVB/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Pumper-221/i-MkzmfZW/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Pumper-333/i-KthC2FL/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Pumper-443/i-wtTsHMF/A"
        ]
    },
    {
        id: "rescue-pumper",
        prefix: "R",
        name: "Rescue Pumper",
        description: "Like the pumper, but with more rescue equipment.",
        aliases: ["Rescue"],
        examples: ["R115", "R227", "R341", "R434"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Rescue-115/i-7cZzpJq/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Rescue-227/i-5HZtRLL/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Rescue-341/i-9vqb6gQ/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Rescue-434/i-jQs7N5H/A"
        ]
    },
    {
        id: "foam-pumper",
        prefix: "PF",
        name: "Foam Pumper",
        description: "Like the pumper, but with firefighting foam in addition to water. Better suited for industrial fires.",
        aliases: [],
        examples: ["PF121"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Foam-Pumper-121/i-JfMGzrf/A"
        ]
    },
    {
        id: "ladder",
        prefix: "L",
        name: "Ladder",
        description: "Has a long, straight aerial ladder and rescue equipment.",
        aliases: ["Ladder Truck", "Truck"],
        examples: ["L226", "L325", "L426"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Incidents/2021/April-72021-Working-Fire-114-Denton-Ave/i-ZNXgzX9/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Ladder-325/i-tJ78MHp/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Ladder-426/i-qFDGFMw/A"
        ]
    },
    {
        id: "aerial-ladder",
        prefix: "A",
        name: "Aerial Ladder",
        description: "Sometimes called a \"quint\". Like the ladder, but also has a water tank and pump like a pumper.",
        aliases: ["Aerial", "Quint", "Truck"],
        examples: ["A113", "A221", "A322", "A411"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Aerial-113/i-TL6f4Jz/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Aerial-221/i-bFLtdjZ/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Aerial-322/i-nthZ8Jm/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Aerial-411/i-7K3SP6j/A"
        ]
    },
    {
        id: "tower",
        prefix: "T",
        name: "Tower",
        description: "Like the ladder or aerial ladder, but has a platform attached to an arm instead.",
        aliases: [],
        examples: ["T114", "T333", "T1"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Tower-114/i-8BKwLSF/A",
            "https://pbs.twimg.com/media/E9Km5zlWYAQi1eM?format=jpg&name=large",
            "https://www.flickr.com/photos/127041560@N03/52071426293/"
        ]
    },
    {
        id: "platform",
        prefix: "PL",
        name: "Platform",
        description: "Like an aerial ladder with a platform attached.",
        aliases: [],
        examples: ["PL432"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Platform-432/i-8PNHNR2/A"
        ]
    },
    {
        id: "squad",
        prefix: "S",
        name: "Squad",
        description: "A heavy rescue vehicle with specialized rescue equipment. For example, they're often \
            called to accidents where vehicle occupants are trapped.",
        aliases: [],
        examples: ["S143", "S232", "S313", "S331", "S445"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Squad-143/i-7SZT62G/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Squad-232/i-djVjtcV/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Squad-313/i-vgSJWjK/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Squad-331/i-vpB3CD7/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Squad-445/i-SB87Rjm/A"
        ]
    },
    {
        id: "highrise",
        prefix: "HR",
        name: "Highrise",
        description: "A vehicle with specialized highrise rescue equipment.",
        aliases: [],
        examples: ["HR114", "HR332"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/High-Rise-114/i-kgjkVPf/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/High-Rise-332/i-W5XgJ3j/A"
        ]
    },
    {
        id: "hazmat",
        prefix: "HZ",
        name: "HazMat",
        description: "A unit trained and equipped to handle hazardous materials incidents, chemical spills, and decontamination.",
        aliases: ["Hazardous Materials"],
        examples: ["HZ145", "HZ323"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Hazmat-145/i-ZQF7mB5/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Haz-Mat-323/i-ndLSqN9/A"
        ]
    },
    {
        id: "hazmat-support",
        prefix: "HS",
        name: "Haz Support",
        description: "Supports HazMat units with additional equipment, supplies, and personnel.",
        aliases: ["Hazardous Materials Support", "HazMat Support"],
        examples: ["HS145", "HS323"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Hazmat-Support-145/i-8RVkWTD/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Haz-Mat-Support-323/i-cVXrh4H/A"
        ]
    },
    {
        id: "fireboat",
        prefix: "FB",
        name: "Fireboat",
        description: "A boat with equipment for firefighting and rescue operations on the water, and supporting \
            operations on the Toronto Islands.",
        aliases: ["Fire Boat", "William Lyon Mackenzie", "William Thornton"],
        examples: ["FB334", "FB334B"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Fireboat-334-William-Lyon-Mackenzie/i-8mvnKDJ/A",
            "https://flickr.com/photos/citatus/48977760997/"
        ]
    },
    {
        id: "command",
        prefix: "CMD",
        name: "Command",
        description: "Used by senior officers to coordinate responses at major incidents. Equipped with communication and incident command tools.",
        aliases: [],
        examples: ["CMD10", "CMD30"],
        example_image_links: [
            "https://pbs.twimg.com/media/E9O7xyPXoAQttgO?format=jpg&name=large",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Command-30/i-Rgjr3SZ/A"
        ]
    },
    {
        id: "chief",
        prefix: "C",
        name: "Chief",
        description: "An officer vehicle used by the Fire Chief, Deputy Chief, District Chief or Platoon Chief \
            to oversee fireground operations and administrative tasks.",
        aliases: ["Car"],
        examples: ["C10", "C11", "C22", "C32", "C43"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Platoon-Chief-10/i-bSzKHzb/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/District-Chief-11/i-4bttngP/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/District-Chief-22/i-xX5HRqX/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/District-Chief-32/i-r7SwBTZ/A",
            "https://cdn.myportfolio.com/359eb651-e4b7-4fe7-a59b-3dc6ac241b77/09ee35d5-39db-4279-a0c1-2ea1ba53d21d_rw_1920.jpg?h=ca4d492742f6cc2b517f442bbee15067"
        ]
    },
    {
        id: "air-light",
        prefix: "LA",
        name: "Air Light",
        description: "Carries extra lighting equipment and air cylinders to refill firefighters' breathing apparatus during extended incidents.",
        aliases: ["Air/Light", "Light and Air"],
        examples: ["LA111", "LA231", "LA325", "LA421"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Air-Light-111/i-37QQP5z/A",
            "https://www.flickr.com/photos/127041560@N03/52147553324/",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/AirLight-325/i-kxj92cR/A",
            "https://pbs.twimg.com/media/FZVE2pbWIAM8etS?format=jpg&name=large"
        ]
    },
    {
        id: "trench-rescue-support",
        prefix: "TRS",
        name: "Trench Rescue Support",
        description: "Supports trench and confined space rescues, with equipment to shore up collapsed areas and ensure rescuer safety.",
        aliases: ["Trench Rescue", "Technical Rescue Support"],
        examples: ["TRS235"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Technical-Rescue-235/i-jdZncs4/A"
        ]
    },
    {
        id: "water-tanker",
        prefix: "WT",
        name: "Water Tanker",
        description: "Transports large amounts of water to areas without hydrants, typically rural or remote locations.",
        aliases: ["Tanker"],
        examples: ["WT211"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Tanker-211/i-CwmNWtB/A"
        ]
    },
    {
        id: "rehab",
        prefix: "REHAB",
        name: "Rehab",
        description: "A unit that provides food, drinks, and rest support to firefighters at prolonged emergency scenes. \
            SUP7 (a.k.a. Support 7) is operated by a volunteer organization called the \
            <a href=\"https://gtmaa.com/\" target=\"_blank\" rel=\"noopener noreferrer\">Greater Toronto Multiple Alarm Association</a>. \
            BOX12 is operated by a volunteer organization called the \
            <a href=\"https://www.instagram.com/box12association\" target=\"_blank\" rel=\"noopener noreferrer\">Box 12 Association</a> \
            since 1948.",
        aliases: ["Rehabilitation", "Canteen Vehicle"],
        examples: ["REHAB01", "SUP7", "BOX12"],
        example_image_links: [
            "https://www.flickr.com/photos/knl-17/45545609244",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Support-7/i-WNqZVpq/A",
            "https://www.flickr.com/photos/iduke/52289434100/"
        ]
    },
    {
        id: "decon",
        prefix: "DE",
        name: "Decon",
        description: "Used to decontaminate personnel and equipment exposed to hazardous materials.",
        aliases: ["Decontamination Unit"],
        examples: ["DE234"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Command-Apparatus/i-2rfC64c/A"
        ]
    },
    {
        id: "mini-pumper",
        prefix: "MP",
        name: "Mini Pumper",
        description: "A smaller, more maneuverable fire engine used in tight urban spaces or for quick response. Carries essential firefighting tools.",
        aliases: [],
        examples: ["MP212"],
        example_image_links: [
            "https://pbs.twimg.com/media/EvrMjSBXYAA3Qll?format=jpg&name=large"
        ]
    },
    {
        id: "fire-investigator",
        prefix: "FI",
        name: "Fire Investigator",
        description: "Responds to determine the origin and cause of fires. These units assist with post-incident analysis and investigations.",
        aliases: [],
        examples: [],
        example_image_links: [
            "https://www.flickr.com/photos/18378305@N00/50909687993",
            "https://www.tfspics.com/Toronto-Fire-Miscellaneous/2020/i-m3WQFsP/A"
        ]
    },
    {
        id: "personnel-transport",
        prefix: "PT",
        name: "Personnel Transport",
        description: "Transports additional firefighting personnel to and from the scene of an incident.",
        aliases: ["Transport"],
        examples: ["PT01"],
        example_image_links: [
            "https://www.flickr.com/photos/iduke/53651215341/"
        ]
    },
    {
        id: "multi-purpose-vehicle",
        prefix: "MPV",
        name: "Multi-Purpose Vehicle",
        description: "A vehicle with extra equipment to support incidents.",
        aliases: [],
        examples: ["MPV341"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Multi-Purpose-Vehicle-341/i-M2wWJG3/A"
        ]
    },
    {
        id: "rapid-attack-vehicle",
        prefix: "V",
        name: "Rapid Attack Vehicle",
        description: "A vehicle that can quickly respond to incidents with specialized equipment for rapid deployment. Operates on the Toronto Islands.",
        aliases: ["Van"],
        examples: ["V335"],
        example_image_links: [
            "https://www.flickr.com/photos/pockets-love/47968988048/"
        ]
    },
    {
        id: "cbrne",
        prefix: "CBRNE",
        name: "CBRNE",
        description: "A highly specialized team trained to handle Chemical, Biological, Radiological, Nuclear, and Explosive threats. \
            They use advanced detection and decontamination equipment to protect the public during hazardous incidents and often work in conjunction with police and medical teams.",
        aliases: ["CBRNE Unit"],
        examples: ["CBRNE"],
        example_image_links: [
            "https://i.ytimg.com/vi/y0Du140WoUY/maxresdefault.jpg"
        ]
    },
    {
        id: "fire-chief",
        prefix: null,
        name: "Fire Chief",
        description: "Top executive officer of Toronto Fire Services. \
            Responsible for the overall leadership, policy direction, and operation of the entire department. \
            Works closely with city officials and other emergency services. \
            Handles strategic planning, budgeting, public safety initiatives, and emergency response readiness across the whole city.",
        aliases: [],
        examples: ["C1"],
        example_image_links: []
    },
    {
        id: "deputy-chief",
        prefix: "C",
        name: "Deputy Chief",
        description: "Reports directly to the Fire Chief. \
            Assists in managing large areas of departmental operations (e.g., Operations, Training, Fire Prevention, Administration). \
            May act as Fire Chief in their absence. \
            Oversees senior staff, including Platoon and District Chiefs depending on their division.",
        aliases: [],
        examples: ["C2", "C3", "C4", "C5"],
        example_image_links: []
    },
    {
        id: "platoon-chief",
        prefix: "C",
        name: "Platoon Chief",
        description: "A shift commander for all firefighting operations citywide during their assigned platoon (or shift). \
            Coordinates the response of multiple fire stations and districts during incidents. \
            Often the highest-ranking officer on duty at night or during major emergencies before senior command arrives. \
            Ensures that resources are effectively deployed during large-scale or multiple incidents.",
        aliases: [],
        examples: ["C10", "C20", "C30", "C40"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/Platoon-Chief-10/i-bSzKHzb/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/Platoon-Chief-20/i-TWZP4MQ/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/Platoon-Chief-30/i-3s4hVQ9/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/Platoon-Chief-40/i-Sjbvqcf/A"
        ]
    },
    {
        id: "district-chief",
        prefix: "C",
        name: "District Chief",
        description: "Oversees a specific group of fire stations within a defined geographic district. \
            Directly supervises fire captains and station crews. \
            Responds to significant incidents in their district and assumes incident command until relieved. \
            Ensures proper staffing, training, and readiness of their assigned stations.",
        aliases: [],
        examples: ["C11", "C23", "C33", "C41"],
        example_image_links: [
            "https://www.tfspics.com/Toronto-Fire-Apparatus/North-Apparatus/District-Chief-11/i-4bttngP/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/East-Apparatus/District-Chief-23/i-D4MQh8S/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/South-Apparatus/District-Chief-33/i-vkdRF2f/A",
            "https://www.tfspics.com/Toronto-Fire-Apparatus/West-Apparatus/District-Chief-41/i-JfL9NSR/A"
        ]
    },
];

const alphabet = "▲ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const activeLetter = ref(""); // No active letter by default, show all
const availableLetters = [
    ...new Set(allGlossaryItems.map(item => item.name[0].toUpperCase()))
];
availableLetters.sort();
availableLetters.unshift("▲");
const searchTerm = ref("");

const getTermsForLetter = (letter) => {
    return filteredTerms.value.filter((type) =>
        type.name.toUpperCase().startsWith(letter)
    );
};

const jumpToLetter = async (letter) => {
    searchTerm.value = "";
    activeLetter.value = letter;

    // Wait for the DOM to update after search term is cleared
    await nextTick();

    if (letter === "▲") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        clearLetterSelection();
    } else {
        const targetId = `glossary-section-${letter}`;
        const element = document.getElementById(targetId);

        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            console.warn(`No terms found for letter ${letter}, cannot scroll.`);
        }
    }
};

// Clear active letter when user starts typing in search
const clearLetterSelection = () => {
    activeLetter.value = "";
};

const filteredTerms = computed(() => {
    let terms = allGlossaryItems;

    if (searchTerm.value) {
        const searchLower = searchTerm.value.toLowerCase();
        terms = terms.filter(
            (term) =>
                (term.prefix &&
                    term.prefix.toLowerCase().includes(searchLower)) ||
                term.name.toLowerCase().includes(searchLower) ||
                (term.aliases &&
                    term.aliases.some((alias) =>
                        alias.toLowerCase().includes(searchLower)
                    )) ||
                (term.examples &&
                    term.examples.some((example) =>
                        example.toLowerCase().includes(searchLower)
                    ))
        );
    }
    return terms.sort((a, b) => a.name.localeCompare(b.name));
});
</script>

<style scoped>
:root {
    --glossary-card-title-bg: #dea061;
    --glossary-card-title-text: #a11128;
    --glossary-section-title: #a11128;
}

.glossary-card-title {
    background-color: var(--glossary-card-title-bg, #dea061);
    color: var(--glossary-card-title-text, #a11128);
    margin: 0 -1rem;
    padding: 0 1rem;
}

.glossary-description {
    padding: 8px 16px 16px 16px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.875rem;
    line-height: 1.5;
    text-align: center;
}

.glossary-description a {
    color: #1976d2;
    text-decoration: none;
}

.glossary-description a:hover {
    text-decoration: underline;
}

.glossary-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.glossary-detail-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 2px;
}

.glossary-detail-label {
    min-width: 110px;
    font-size: 0.95em;
    color: #444;
    padding-right: 8px;
    flex-shrink: 0;
    text-align: left;
}

.glossary-detail-value {
    flex: 1;
    font-size: 0.95em;
    text-align: left;
    word-break: break-word;
}

/* Responsive layout for small screens */
@media (max-width: 600px) {
    .glossary-description {
        text-align: left;
    }

    .glossary-detail-row {
        flex-direction: column;
        margin-bottom: 8px;
    }

    .glossary-detail-label {
        min-width: auto;
        padding-right: 0;
        padding-bottom: 2px;
        font-weight: 600;
    }

    .glossary-detail-value {
        flex: none;
        padding-left: 0;
    }
}

.glossary-item-wrapper {
    width: 100%;
    margin: 0 auto;
    padding: 0 1rem;
    margin-bottom: 1.5rem;
}

@media (min-width: 768px) {
    .glossary-item-wrapper {
        width: 90%;
        max-width: 1000px;
        padding: 0;
        margin-bottom: 2rem;
    }

    .glossary-card-title {
        margin: 0;
        padding: 0 1rem;
    }
}

.glossary-page {
    display: flex;
}

.glossary-section-title {
    color: var(--glossary-section-title-text, #a11128);
}

.glossary-sidebar {
    position: fixed;
    /* Offset for fixed app bars (80px + 40px = 120px) */
    top: 120px;
    left: 0;
    height: calc(100vh - 120px);
    width: 60px;
    padding: 20px 0;
    background-color: #dea061;
    border-right: 1px solid #eee;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    z-index: 20
}

@media (max-width: 767px) {
    .glossary-sidebar {
        top: 80px;
        height: calc(100vh - 80px);
    }
}

.glossary-main-content {
    flex-grow: 1;
    margin-left: 60px;
    padding: 20px 40px;
}

h1 {
    margin-top: 0;
    margin-bottom: 30px;
    color: #a11128;
}

.glossary-section {
    /* Offset for fixed app bars (80px + 40px = 120px) */
    scroll-margin-top: 120px;
}

.search-bar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    padding-top: 10px;
    padding-bottom: 10px;
    margin-bottom: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.search-bar input {
    width: 100%;
    padding: 12px 15px;
    font-size: 1.1em;
    border: 1px solid #ccc;
    border-radius: 8px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
}

.search-bar input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

@media (max-width: 600px) {
    .search-bar input {
        font-size: 0.95em;
    }
}
</style>